import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import { env } from './config/env';
import './i18n';
import { ThemeProvider } from './components/theme-provider';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401 is handled globally by the axios interceptor; retrying it just
      // delays the redirect to the login page.
      retry: (failureCount, error) =>
        failureCount < 2 &&
        (error as { status?: number })?.status !== 401,
    },
  },
});

/*
 * Vite substitutes %VITE_ADMIN_TITLE% into the tab title at build time. A
 * deploy that never set the variable leaves the token itself on screen, and
 * one that sets it empty leaves the tab blank — the same class of failure
 * `raw()` in config/env.ts exists to catch. Fall back to the validated value
 * in both cases rather than shipping either.
 */
if (!document.title || document.title.startsWith('%')) {
  document.title = env.adminTitle;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <App />
          <Toaster richColors closeButton position="bottom-right" />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);
